import React, { useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { change } from 'redux-form';
import { compose } from 'redux';
import sortBy from 'lodash/sortBy';
import { Grid, Field } from 'nallian-shared-ui/lib/components';
import lookupService from 'nallian-shared-ui/lib/hoc/lookupService';
import {
  InputFieldValueComponent,
  SelectFieldValueComponent,
  TextAreaFieldValueComponent,
} from 'nallian-shared-ui/lib/components/redux-form-semantic-ui';
import apiRoutes from '../../../../../app/api/routes';

const PrincipalDetailsTab = ({ timezonesOverviewFetch, languagesFetch }) => {
  const { t } = useTranslation();

  const dispatch = useDispatch();

  const timezoneOptions = useMemo(
    () => (timezonesOverviewFetch.value || []).map(v => ({ key: v, value: v, text: v })),
    [timezonesOverviewFetch.value]
  );

  const languageOptions = useMemo(
    () =>
      sortBy(languagesFetch.value || [], 'name').map(l => ({
        key: l.id,
        value: l.id,
        text: l.nativeName,
      })),
    [languagesFetch.value]
  );

  useEffect(() => {
    // if they change we need to check for default browser values to check
    const browserLanguage = (navigator.language || navigator.userLanguage).slice(0, 2);
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    if (timezoneOptions.find(tz => tz.value === browserTimezone)) {
      dispatch(change('FormOrganisationNew', `principal.timeZone`, browserTimezone));
    }

    if (languageOptions.find(l => l.value === browserLanguage)) {
      dispatch(change('FormOrganisationNew', `principal.languageId`, browserLanguage));
    }
  }, [languageOptions, timezoneOptions, dispatch]);

  return (
    <Grid margin="1rem -1rem 0 -1rem">
      <Grid.Column mobile={8} tablet={8} computer={8}>
        <Field
          fluid
          label={t('fullName')}
          name="principal.fullName"
          valueComponent={InputFieldValueComponent}
        />
      </Grid.Column>
      <Grid.Column mobile={8} tablet={8} computer={8}>
        <Field
          fluid
          label={t('emailAddress')}
          type="email"
          name="principal.primaryEmailAddress"
          valueComponent={InputFieldValueComponent}
        />
      </Grid.Column>
      <Grid.Column mobile={8} tablet={8} computer={8}>
        <Field
          fluid
          search
          label={t('language')}
          name="principal.languageId"
          type="select"
          options={languageOptions}
          valueComponent={SelectFieldValueComponent}
        />
      </Grid.Column>
      <Grid.Column mobile={8} tablet={8} computer={8}>
        <Field
          fluid
          search
          clearable
          label={t('timezone')}
          name="principal.timeZone"
          options={timezoneOptions}
          valueComponent={SelectFieldValueComponent}
        />
      </Grid.Column>
      <Grid.Column mobile={16} tablet={16} computer={16}>
        <Field
          fluid
          label={t('inviteFreeText')}
          type="text"
          name="principal.inviteRequest.freeText"
          valueComponent={TextAreaFieldValueComponent}
        />
      </Grid.Column>
    </Grid>
  );
};

PrincipalDetailsTab.propTypes = {
  timezonesOverviewFetch: PropTypes.object.isRequired,
  languagesFetch: PropTypes.object.isRequired,
};

PrincipalDetailsTab.defaultProps = {};

export default compose(
  lookupService({
    resource: 'timezonesOverview',
    url: apiRoutes.timezones(),
  }),
  lookupService({
    resource: 'languages',
    url: apiRoutes.getLanguages(),
  })
)(PrincipalDetailsTab);
